import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { ChevronDown, ChevronUp, Hourglass, RefreshCw, ShieldAlert } from "lucide-react";

import { HUB_INFRASTRUCTURE_BLIP_EVENT, HUB_UPDATING_BANNER_MESSAGE } from "@/lib/api";
import { useHubToast } from "@/components/ui/HubToast";
import { hubDevStackHealthzUrl } from "@/lib/hubApiOrigin";
import {
  authOutageActive,
  authOutageBannerText,
  getHubBackendDegradedState,
  secondsUntilAuthOutageRetry,
  slowRequestBannerText,
  subscribeHubBackendDegraded,
} from "@/lib/hubBackendDegraded";
import { HUB_LIFECYCLE_RECONNECTED_EVENT } from "@/lib/hubLifecycleEvents";
import {
  getHubDbPoolHolderStreamState,
  subscribeHubDbPoolHolderStream,
} from "@/lib/hubDbPoolHolderStream";
import {
  BROWSER_WAITER_SECTION_HELP,
  BROWSER_WAITER_SECTION_TITLE,
  BROWSER_WAITER_EMPTY_TEXT,
  POOL_HOLDER_EMPTY_TEXT,
  POOL_HOLDER_SECTION_HELP,
  POOL_HOLDER_SECTION_TITLE,
  POOL_HOLDER_STREAM_DISCONNECTED_TEXT,
  POOL_HOLDER_STREAM_RECONNECTING_TEXT,
  PROCESS_WAITER_EMPTY_TEXT,
  PROCESS_WAITER_SECTION_HELP,
  PROCESS_WAITER_SECTION_TITLE,
  poolHolderBannerRows,
  poolHolderCapacityText,
  processWaiterBannerRows,
  slowRequestBannerRows,
} from "@/lib/slowRequestBanner";
import { HUB_CLIENT_BUILD_VERSION } from "@/lib/hubBuildVersion";
import { hubSupportTicketPagePath } from "@/lib/navigationTrail";
import {
  flushPendingSlowHubSupportTicket,
  hasSlowHubAutoReportMarker,
  isHubDevInstance,
  shouldAutoReportSlowHub,
  slowHubSupportDedupeKey,
  submitSlowHubSupportTicket,
} from "@/lib/slowRequestSupportTicket";
import { tabWakeSuppressesSlowHubAutoReport } from "@/lib/tabWakeStaleFetch";
import { useDevStackRestartMessage } from "@/lib/useDevStackRestartFlags";

/**
 * Top-of-screen status while the local stack restarts or production hub blips.
 *
 * - **Local dev:** `dev-stack-restart-signal.gen.ts` is rewritten before registry reload;
 *   Vite HMR picks it up (no guessing from HTTP 500).
 * - **Production:** `hub:infrastructure-blip` after gateway errors, then polls routed `/healthz`.
 *   Banner appears only when recovery exceeds a short threshold so blue-green handoffs
 *   stay invisible during normal cutover.
 */
const HEALTH_POLL_MS = 1_000;
const BANNER_SHOW_DELAY_MS = 2_500;
const SLOW_REQUEST_DETAILS_ID = "hub-slow-request-details";

/** Hub Dev stacks never auto-file slow-hub tickets (API restarts under tsx watch look slow). */
const SLOW_HUB_AUTO_REPORT_DISABLED =
  typeof window !== "undefined" &&
  isHubDevInstance({
    clientBuildVersion: HUB_CLIENT_BUILD_VERSION,
    viteDev: import.meta.env.DEV === true,
    hostname: window.location.hostname,
  });

export function HubUpdatingBanner() {
  const devRestartMessage = useDevStackRestartMessage();
  const { showToast } = useHubToast();
  const [infraMessage, setInfraMessage] = useState<string | null>(null);
  const degraded = useSyncExternalStore(subscribeHubBackendDegraded, getHubBackendDegradedState);
  const holderStream = useSyncExternalStore(
    subscribeHubDbPoolHolderStream,
    getHubDbPoolHolderStreamState,
  );
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [slowExpanded, setSlowExpanded] = useState(false);
  const [reportingSlowHub, setReportingSlowHub] = useState(false);
  const autoReportStartedRef = useRef(false);

  const degradedActive = degraded.authOutage !== null || degraded.slowRequests.length > 0;
  const slowRows = slowRequestBannerRows(degraded.slowRequests, nowMs);
  const slowElapsedSeconds =
    slowRows.length === 0 ? null : Math.max(...slowRows.map((row) => row.elapsedSeconds));
  const slowMessage =
    slowElapsedSeconds !== null
      ? slowRequestBannerText(slowElapsedSeconds, slowRows.length)
      : null;
  useEffect(() => {
    if (!degradedActive) {
      return;
    }
    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [degradedActive]);

  useEffect(() => {
    if (slowRows.length === 0) {
      setSlowExpanded(false);
    }
  }, [slowRows.length]);

  async function reportSlowHub(trigger: "auto" | "manual"): Promise<void> {
    if (reportingSlowHub) {
      return;
    }
    setReportingSlowHub(true);
    try {
      const result = await submitSlowHubSupportTicket({
        slowRows: degraded.slowRequests,
        holderSnapshot: holderStream.snapshot,
        holderStreamStatus: holderStream.status,
        nowMs,
        trigger,
      });
      if (result.ok && result.ticketId) {
        const prefix =
          trigger === "auto"
            ? result.dedupeSkipped
              ? "Slow hub already reported"
              : "Slow hub auto-reported"
            : result.dedupeSkipped
              ? "Support ticket already open"
              : "Support ticket sent";
        showToast({
          message: `${prefix} (#${result.ticketId}).`,
          tone: "success",
        });
        return;
      }
      if (result.devOriginSkipped) {
        // Server-side backstop for automatic reports from Hub Dev stacks; nothing to show.
        return;
      }
      if (result.skippedClientNetwork) {
        showToast({
          message:
            "This browser cannot reach the hub right now. Check your network connection. No support ticket was sent.",
          tone: "warning",
        });
        return;
      }
      if (result.queuedLocally) {
        showToast({
          message:
            "Could not reach the hub yet. Your report is saved in this browser and will retry automatically.",
          tone: "warning",
        });
        return;
      }
      showToast({
        message: result.errorMessage ?? "Could not send support ticket.",
        tone: "error",
      });
    } finally {
      setReportingSlowHub(false);
    }
  }

  useEffect(() => {
    if (slowRows.length === 0 || slowElapsedSeconds === null) {
      autoReportStartedRef.current = false;
      return;
    }
    if (SLOW_HUB_AUTO_REPORT_DISABLED) {
      return;
    }
    if (tabWakeSuppressesSlowHubAutoReport(nowMs)) {
      return;
    }
    if (!shouldAutoReportSlowHub(slowRows.length, slowElapsedSeconds)) {
      return;
    }
    const pagePath = hubSupportTicketPagePath(window.location.pathname, window.location.search);
    const dedupeKey = slowHubSupportDedupeKey(pagePath, nowMs);
    if (hasSlowHubAutoReportMarker(dedupeKey) || autoReportStartedRef.current) {
      return;
    }
    autoReportStartedRef.current = true;
    void reportSlowHub("auto");
  }, [slowRows.length, slowElapsedSeconds, nowMs, degraded.slowRequests, holderStream.snapshot, holderStream.status]);

  useEffect(() => {
    if (slowRows.length > 0) {
      return;
    }
    void flushPendingSlowHubSupportTicket().then((result) => {
      if (result?.ok && result.ticketId) {
        showToast({
          message: `Saved slow-hub report sent (#${result.ticketId}).`,
          tone: "success",
        });
      }
    });
  }, [slowRows.length, showToast]);

  useEffect(() => {
    if (slowRows.length === 0) {
      return;
    }
    const timer = window.setInterval(() => {
      void flushPendingSlowHubSupportTicket().then((result) => {
        if (result?.ok && result.ticketId) {
          showToast({
            message: `Saved slow-hub report sent (#${result.ticketId}).`,
            tone: "success",
          });
        }
      });
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [slowRows.length, showToast]);

  const authOutageMessage = authOutageActive(degraded, nowMs)
    ? authOutageBannerText(secondsUntilAuthOutageRetry(degraded, nowMs))
    : null;

  const message = devRestartMessage ?? infraMessage;

  useEffect(() => {
    let healthPollActive = false;
    let healthTimer: number | undefined;
    let showDelayTimer: number | undefined;
    let bannerVisible = false;

    const clearHealthTimer = () => {
      if (healthTimer !== undefined) {
        window.clearTimeout(healthTimer);
        healthTimer = undefined;
      }
    };

    const clearShowDelayTimer = () => {
      if (showDelayTimer !== undefined) {
        window.clearTimeout(showDelayTimer);
        showDelayTimer = undefined;
      }
    };

    const hideInfraBanner = () => {
      bannerVisible = false;
      clearShowDelayTimer();
      setInfraMessage((current) =>
        current === HUB_UPDATING_BANNER_MESSAGE ? null : current,
      );
    };

    const pollHealthUntilOk = () => {
      void (async () => {
        let healthy = false;
        try {
          const response = await fetch(hubDevStackHealthzUrl(), { cache: "no-store" });
          healthy = response.ok;
        } catch {
          healthy = false;
        }
        if (healthy) {
          hideInfraBanner();
          healthPollActive = false;
          clearHealthTimer();
          return;
        }
        healthTimer = window.setTimeout(pollHealthUntilOk, HEALTH_POLL_MS);
      })();
    };

    const onInfrastructureBlip = () => {
      if (!healthPollActive) {
        healthPollActive = true;
        pollHealthUntilOk();
      }
      if (bannerVisible || showDelayTimer !== undefined) {
        return;
      }
      showDelayTimer = window.setTimeout(() => {
        showDelayTimer = undefined;
        bannerVisible = true;
        setInfraMessage(HUB_UPDATING_BANNER_MESSAGE);
      }, BANNER_SHOW_DELAY_MS);
    };

    const onLifecycleReconnected = () => {
      hideInfraBanner();
      healthPollActive = false;
      clearHealthTimer();
    };

    window.addEventListener(HUB_INFRASTRUCTURE_BLIP_EVENT, onInfrastructureBlip);
    window.addEventListener(HUB_LIFECYCLE_RECONNECTED_EVENT, onLifecycleReconnected);

    return () => {
      window.removeEventListener(HUB_INFRASTRUCTURE_BLIP_EVENT, onInfrastructureBlip);
      window.removeEventListener(HUB_LIFECYCLE_RECONNECTED_EVENT, onLifecycleReconnected);
      clearHealthTimer();
      clearShowDelayTimer();
    };
  }, []);

  if (message === null && authOutageMessage === null && slowMessage === null) {
    return null;
  }

  // Priority: dev restart / infrastructure > auth outage > slow request.
  const primary =
    message !== null
      ? { text: message, kind: "infra" as const }
      : authOutageMessage !== null
        ? { text: authOutageMessage, kind: "auth" as const }
        : { text: slowMessage as string, kind: "slow" as const };

  const holderSnapshot = holderStream.snapshot;
  const holderRows =
    holderSnapshot !== null ? poolHolderBannerRows(holderSnapshot, nowMs) : [];
  const holderCapacity = holderSnapshot !== null ? poolHolderCapacityText(holderSnapshot) : null;
  const processWaiterRows =
    holderSnapshot !== null ? processWaiterBannerRows(holderSnapshot, nowMs) : [];
  const holderStreamNote =
    holderSnapshot !== null && holderStream.status !== "connected"
      ? POOL_HOLDER_STREAM_RECONNECTING_TEXT
      : holderSnapshot === null
        ? POOL_HOLDER_STREAM_DISCONNECTED_TEXT
        : null;

  return (
    <>
      {devRestartMessage !== null ? (
        <div
          className="pointer-events-none fixed inset-0 z-[250] bg-slate-900/10"
          aria-hidden="true"
        />
      ) : null}
      <div
        role="status"
        aria-live="polite"
        className="fixed inset-x-0 top-0 z-[300] flex justify-center px-4 pt-3"
      >
        {primary.kind === "slow" ? (
          <div className="w-full max-w-2xl overflow-hidden rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-900 shadow-sm">
            <div className="flex items-stretch">
              <button
                type="button"
                aria-expanded={slowExpanded}
                aria-controls={SLOW_REQUEST_DETAILS_ID}
                onClick={() => setSlowExpanded((current) => !current)}
                className="flex min-w-0 flex-1 items-center gap-2 px-4 py-2 text-left hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-0"
              >
                <Hourglass className="h-4 w-4 shrink-0 text-slate-600" aria-hidden="true" />
                <span className="min-w-0 flex-1">{primary.text}</span>
                {slowExpanded ? (
                  <ChevronUp className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
                ) : (
                  <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
                )}
              </button>
              <button
                type="button"
                disabled={reportingSlowHub}
                onClick={() => {
                  void reportSlowHub("manual");
                }}
                className="shrink-0 border-l border-slate-200 px-3 py-2 text-xs font-medium text-brand-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {reportingSlowHub ? "SendingΓÇª" : "Report to support"}
              </button>
            </div>
            {slowExpanded ? (
              <div id={SLOW_REQUEST_DETAILS_ID} className="border-t border-slate-200 px-4 py-2">
                <section>
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                    {POOL_HOLDER_SECTION_TITLE}
                  </h2>
                  {holderCapacity !== null ? (
                    <p className="mt-1 text-xs text-slate-600">{holderCapacity}</p>
                  ) : null}
                  <p className="mt-1 text-xs text-slate-600">{POOL_HOLDER_SECTION_HELP}</p>
                  {holderStreamNote !== null ? (
                    <p className="mt-2 text-xs text-slate-600">{holderStreamNote}</p>
                  ) : null}
                  {holderSnapshot !== null && holderRows.length === 0 ? (
                    <p className="mt-2 text-xs text-slate-600">{POOL_HOLDER_EMPTY_TEXT}</p>
                  ) : null}
                  {holderRows.length > 0 ? (
                    <ul className="mt-2 space-y-1.5">
                      {holderRows.map((row) => (
                        <li key={`holder-${row.id}`} className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-slate-800">{row.title}</div>
                            {row.pathDisplay ? (
                              <code className="block break-all font-mono text-xs text-slate-600">
                                {row.pathDisplay}
                              </code>
                            ) : null}
                          </div>
                          <span className="shrink-0 tabular-nums text-slate-700">
                            {row.elapsedSeconds}s
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
                <section className="mt-3 border-t border-slate-200 pt-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                    {PROCESS_WAITER_SECTION_TITLE}
                  </h2>
                  <p className="mt-1 text-xs text-slate-600">{PROCESS_WAITER_SECTION_HELP}</p>
                  {holderSnapshot !== null && processWaiterRows.length === 0 ? (
                    <p className="mt-2 text-xs text-slate-600">{PROCESS_WAITER_EMPTY_TEXT}</p>
                  ) : null}
                  {processWaiterRows.length > 0 ? (
                    <ul className="mt-2 space-y-1.5">
                      {processWaiterRows.map((row) => (
                        <li key={`process-waiter-${row.id}`} className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-slate-800">{row.title}</div>
                            {row.userLabel ? (
                              <div className="text-xs text-slate-700">{row.userLabel}</div>
                            ) : null}
                            {row.pathDisplay ? (
                              <code className="block break-all font-mono text-xs text-slate-600">
                                {row.pathDisplay}
                              </code>
                            ) : null}
                          </div>
                          <span className="shrink-0 tabular-nums text-slate-700">
                            {row.elapsedSeconds}s
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
                <section className="mt-3 border-t border-slate-200 pt-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    {BROWSER_WAITER_SECTION_TITLE}
                  </h2>
                  <p className="mt-1 text-xs text-slate-600">{BROWSER_WAITER_SECTION_HELP}</p>
                  {slowRows.length === 0 ? (
                    <p className="mt-2 text-xs text-slate-600">{BROWSER_WAITER_EMPTY_TEXT}</p>
                  ) : (
                    <ul className="mt-2 space-y-1.5">
                      {slowRows.map((row) => (
                        <li key={row.id} className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {row.friendlyLabel ? (
                              <div className="font-medium text-slate-800">{row.friendlyLabel}</div>
                            ) : null}
                            <code className="block break-all font-mono text-xs text-slate-600">
                              {row.pathDisplay}
                            </code>
                          </div>
                          <span className="shrink-0 tabular-nums text-slate-700">{row.elapsedSeconds}s</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-950 shadow-sm">
            {primary.kind === "auth" ? (
              <ShieldAlert className="h-4 w-4 text-amber-700" aria-hidden="true" />
            ) : (
              <RefreshCw className="h-4 w-4 animate-spin text-amber-700" aria-hidden="true" />
            )}
            <span>{primary.text}</span>
          </div>
        )}
      </div>
    </>
  );
}
